import React, { useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'

import CreateProductStoreDialog from '@shared/dialogs/create-product-store-dialog'
import MaterialTable, { MTableToolbar } from '@material-table/core'
import { Paper } from '@mui/material'
import { makeStyles } from 'tss-react/mui'

import {
  createProductStore,
  removeProductAtStore,
  setDisabledForProductAtStore,
  setDisabledForVariantAtStore,
  setPriceForVariantAtStore,
} from '@services/product-service'
import { fetchProductsStoresForStoreId } from '@services/store-service'
import { fetchVendors } from '@services/vendor-service'

import AddDefaultProducts from './add-default-products'
import DisableCell from './disable-cell'
import EmptyMenu from './empty-menu'
import { DisabledState } from '@types'
import { getAllVendorProducts } from '@store/actions/vendor-products'
import { getProductsForStore } from '@store/selectors/products-stores'
import { getVendorRegions } from '@store/actions/vendor-regions'
import { selectVendorProducts } from '@store/selectors/vendor-products'
import { setFlashMessage } from '@store/reducers/flash-message/actions'

const useStyles = makeStyles()(() => ({
  row: {
    display: 'flex',
    paddingRight: 8,
    alignItems: 'center',
  },
  table: {
    '& thead th:last-child, & tbody td:last-child': {
      paddingRight: 16,
    },
  },
}))

// What just happened, for a state the server confirmed it applied.
const DISABLE_MESSAGES = {
  [DisabledState.none]: 're-enabled',
  [DisabledState.untilEndOfDay]: 'disabled until end of day',
  [DisabledState.indefinitely]: 'disabled indefinitely',
}

// Where the row stands now, for a request the server declined to apply.
const DISABLE_STATE_LABELS = {
  [DisabledState.none]: 'available',
  [DisabledState.untilEndOfDay]: 'disabled until end of day',
  [DisabledState.indefinitely]: 'disabled indefinitely',
}

const disableResult = (noun, requested, actual) =>
  actual === requested
    ? [`${noun} ${DISABLE_MESSAGES[actual]}`, 'success']
    : [`${noun} unchanged — it is ${DISABLE_STATE_LABELS[actual]}`, 'info']

const mapStateToProps = (state, ownProps) => {
  return {
    products: getProductsForStore(state, ownProps.storeId),
    vendors: state.Vendors,
    vendorProducts: selectVendorProducts(state),
  }
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  actions: {
    createProductStore: (data) => dispatch(createProductStore(data)),
    fetchProductsStores: () =>
      dispatch(fetchProductsStoresForStoreId(ownProps.storeId)),
    fetchVendors: fetchVendors(dispatch),
    removeProductAtStore: (productId) =>
      dispatch(removeProductAtStore(ownProps.storeId, productId)),
    setStoreVariantPrice: (variantId, price) =>
      dispatch(setPriceForVariantAtStore(ownProps.storeId, variantId, price)),
    setProductDisabled: (productId, state) =>
      dispatch(
        setDisabledForProductAtStore(ownProps.storeId, productId, state),
      ),
    setVariantDisabled: (variantId, state) =>
      dispatch(
        setDisabledForVariantAtStore(ownProps.storeId, variantId, state),
      ),
    setFlashMessage: (message, variant) =>
      dispatch(setFlashMessage(message, variant)),
    getVendorProducts: () => dispatch(getAllVendorProducts()),
    getVendorRegions: () => dispatch(getVendorRegions()),
  },
})

function MenuWidget(props) {
  const { actions, products } = props
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [createDialogLoading, setCreateDialogLoading] = useState(false)
  const [hasFetchedData, setHasFetchedData] = useState(false)
  const { classes: styles, cx } = useStyles()
  useEffect(() => {
    if (!hasFetchedData) {
      setHasFetchedData(true)

      Promise.all([actions.fetchProductsStores()]).catch((error) =>
        actions.setFlashMessage(error.message, 'error'),
      )
    }
  }, [hasFetchedData])

  const tableData = useMemo(() => {
    const parents = products.map((p) => ({
      id: p.product.id,
      productId: p.product.id,
      name: p.product.name,
      product_type: p.product.product_type,
      sku: null,
      price: null,
      disabledState: p.productStore.disabled?.state ?? DisabledState.none,
      anyVariantsDisabled: p.productStore.disabled?.any_variants ?? false,
    }))
    return parents
      .concat(
        products.map((p) => {
          return p.product.variants.map((variant) => {
            const priceRow = p.productStore.prices.find(
              (x) => x.variant_id == variant.id,
            )

            return {
              id: `v${variant.id}`,
              name: null,
              variantId: variant.id,
              productId: p.product.id,
              sku: variant.sku,
              parentId: p.product.id,
              price: priceRow?.price ?? null,
              disabledState: priceRow?.disabled?.state ?? DisabledState.none,
              allVariantsDisabled:
                p.productStore.disabled?.all_variants ?? false,
            }
          })
        }),
      )
      .flat()
  }, [products])

  const handleSetProductDisabled = async (productId, state) => {
    try {
      const productStore = await actions.setProductDisabled(productId, state)
      const actualState = productStore.disabled?.state ?? DisabledState.none

      actions.setFlashMessage(...disableResult('Product', state, actualState))
    } catch (e) {
      actions.setFlashMessage(e.message, 'error')
      // If the menu changed while we were on the page
      actions.fetchProductsStores().catch(() => {})
    }
  }

  const handleSetVariantDisabled = async (variantId, state) => {
    try {
      const productStore = await actions.setVariantDisabled(variantId, state)
      const actualState =
        productStore.prices.find((x) => x.variant_id === variantId)?.disabled
          ?.state ?? DisabledState.none

      actions.setFlashMessage(...disableResult('Variant', state, actualState))
    } catch (e) {
      actions.setFlashMessage(e.message, 'error')
      actions.fetchProductsStores().catch(() => {})
    }
  }

  return (
    <div className={cx(props.className, styles.table)}>
      <CreateProductStoreDialog
        hideProduct={false}
        hideStore
        initialData={{
          storeId: props.storeId,
        }}
        loading={createDialogLoading ? true : undefined}
        onClose={() => setCreateDialogOpen(false)}
        onConfirm={(data) => {
          setCreateDialogLoading(true)
          actions
            .createProductStore(data)
            .then(() => {
              setCreateDialogLoading(false)
              setCreateDialogOpen(false)
              actions.setFlashMessage('Created Product at Store', 'success')
            })
            .catch((err) => {
              setCreateDialogLoading(false)
              actions.setFlashMessage(err.message, 'error')
            })
        }}
        open={createDialogOpen}
      />
      {products.length === 0 ? (
        <Paper>
          {props.editable && (
            <EmptyMenu
              onAddProduct={() => setCreateDialogOpen(true)}
              storeId={props.storeId}
            />
          )}
        </Paper>
      ) : (
        <MaterialTable
          actions={
            props.editable
              ? [
                  {
                    icon: 'add',
                    tooltip: 'Add Product',
                    isFreeAction: true,
                    onClick: () => setCreateDialogOpen(true),
                  },
                ]
              : undefined
          }
          columns={[
            {
              title: 'Product',
              field: 'name',
              editable: 'never',
            },
            {
              title: 'Type',
              field: 'product_type',
              editable: 'never',
            },
            {
              title: 'SKU',
              field: 'sku',
              editable: 'never',
            },
            {
              title: 'Price (cents)',
              field: 'price',
            },
            {
              title: 'Disabled State',
              sorting: false,
              render: function renderDisable(rowData) {
                const isVariant = !!rowData.parentId

                return (
                  <DisableCell
                    allVariantsDisabled={rowData.allVariantsDisabled}
                    anyVariantsDisabled={rowData.anyVariantsDisabled}
                    canDisableIndefinitely={props.canDisableIndefinitely}
                    canDisableProducts={props.canDisableProducts}
                    isVariant={isVariant}
                    onChange={
                      isVariant
                        ? (state) =>
                            handleSetVariantDisabled(rowData.variantId, state)
                        : (state) =>
                            handleSetProductDisabled(rowData.productId, state)
                    }
                    state={rowData.disabledState}
                    storeTimeZone={props.storeTimeZone}
                  />
                )
              },
            },
          ]}
          components={{
            Toolbar: function renderToolbar(toolbarProps) {
              return (
                <div className={styles.row}>
                  <MTableToolbar {...toolbarProps} />
                  {props.editable && (
                    <AddDefaultProducts storeId={props.storeId} />
                  )}
                </div>
              )
            },
          }}
          data={tableData}
          editable={
            props.editable
              ? {
                  isEditHidden: (x) => !x.parentId,
                  isDeleteHidden: (x) => x.parentId,
                  onRowUpdate: async ({ variantId, price }) => {
                    const newPrice = parseInt(price, 10)
                    if (isNaN(newPrice)) {
                      return
                    } else {
                      return actions
                        .setStoreVariantPrice(variantId, newPrice)
                        .then(() =>
                          actions.setFlashMessage(
                            'Menu item updated',
                            'success',
                          ),
                        )
                        .catch((e) =>
                          actions.setFlashMessage(e.message, 'error'),
                        )
                    }
                  },
                  onRowDelete: async ({ productId }) => {
                    return actions
                      .removeProductAtStore(productId)
                      .then(() =>
                        actions.setFlashMessage('Product removed', 'success'),
                      )
                      .catch((e) => actions.setFlashMessage(e.message, 'error'))
                  },
                }
              : undefined
          }
          options={{
            actionsColumnIndex: 5,
          }}
          parentChildData={(row, rows) =>
            rows.find((a) => a.id === row.parentId)
          }
          title='Menu'
        />
      )}
    </div>
  )
}

export default connect(mapStateToProps, mapDispatchToProps)(MenuWidget)
